import { Api } from '@/api';
import { useWorkflowDefinitionsStore } from '@/stores/workflow/workflowDefinitions';
import { computed, ref, watch } from 'vue';
import { toast } from 'vue-sonner';

import { useLoadingSpinnerStore } from '@/stores/loadingSpinner.ts';
import { useRouter } from 'vue-router';
import { validateWorkflow } from '../workflowValidator';

export const useNewWorkflow = () => {
  const workflowDefinitionsStore = useWorkflowDefinitionsStore();
  const loadingSpinnerStore = useLoadingSpinnerStore();
  const router = useRouter();

  const name = ref<string>('');
  const description = ref<string>('');
  const selectedTriggerOption = ref<string>('');

  const triggerOptions = computed(() =>
    workflowDefinitionsStore.workflowDefinitions.map(definition => definition.trigger.name)
  );

  watch(
    () => workflowDefinitionsStore.workflowDefinitions,
    definitions => {
      if (definitions.length > 0) selectedTriggerOption.value = definitions[0].trigger.name;
    },
    { immediate: true }
  );

  const createWorkflow = async () => {
    const errors = validateWorkflow({ name: name.value, description: description.value });
    if (errors.length > 0) {
      showErrorToast(errors);
      loadingSpinnerStore.hideLocalLoadingSpinner();
      return;
    }

    const selectedWorkflow = workflowDefinitionsStore.workflowDefinitions.find(definition => {
      return definition.trigger.name === selectedTriggerOption.value;
    })!;

    const response = await Api.Workflows.createWorkflow({
      name: name.value,
      description: description.value,
      trigger_definition_key: selectedWorkflow.trigger.key,
    });
    loadingSpinnerStore.hideLocalLoadingSpinner();
    if (response.status === 'success') {
      showSuccessToast();
      router.push({ name: 'WorkflowDetail', params: { id: response.data.id } });
    } else {
      showErrorToast(response.messages);
    }
  };

  const showSuccessToast = () => {
    const description = 'Your workflow was successfully created.';
    toast.success('Workflow Created', { description });
  };

  const showErrorToast = (errors: string[]) => {
    const description = errors?.join(', ') || 'An error occurred while creating the workflow.';
    toast.error('Error Creating Workflow', { description });
  };

  return {
    name,
    description,
    selectedTriggerOption,
    triggerOptions,
    createWorkflow,
  };
};
